// NeedCandidatePreviewPage — the employer "Gå igenom som kandidat" journey.
//
// A focused, full-bleed surface (outside DashboardShell, like the course player)
// that shows a posting exactly as a candidate sees it — the shared `NeedView` —
// PLUS its required courses as launchable cards. Clicking a course opens the
// real player (preview), so the employer can walk the whole candidate flow:
// read the annons → click into a förkunskapsmodul → go through it → come back.
//
// This is the employer preview consumer of the shared candidate-journey surface;
// the candidate's real flow (JobDetailPage → player) and the future public job
// page (#265) are the other two.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, BookOpen, ChevronRight, GraduationCap, Send } from 'lucide-react';
import { needApi } from '@/lib/api/needApi';
import { prerequisiteApi } from '@/lib/api/prerequisiteApi';
import { moduleApi } from '@/lib/api/moduleApi';
import type { Need } from '@/lib/models/need';
import NeedView, { needToView } from '@/components/needs/NeedView';
import { useEmployer } from '@/contexts/EmployerContext';
import { IconChip } from '@/components/custom-ui/IconChip';
import { LoadingState, ErrorState } from '@/components/custom-ui';

const NEXILINK_WORDMARK_SRC = 'https://cdn.builder.io/api/v1/image/assets/89bc6265b1a84995b7b85b1453a49e93/e937596a79e27aedd1ca37344ef2209cd6e8c5da2cc900e6c0f5467b6176325e?apiKey=89bc6265b1a84995b7b85b1453a49e93&';

interface RequiredCourse { id: string; name: string }

const NeedCandidatePreviewPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { employerDetails } = useEmployer();

    const [need, setNeed] = useState<Need | null>(null);
    const [courses, setCourses] = useState<RequiredCourse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 4);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => { if (id) load(); }, [id]);

    const load = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const loadedNeed = await needApi.getNeedById(id);
            setNeed(loadedNeed);
            try {
                const [required, modules] = await Promise.all([
                    prerequisiteApi.getRequiredModules(id),
                    moduleApi.getMyModules(),
                ]);
                const nameById = new Map((modules || []).map((m) => [m.id, m.module_name]));
                setCourses(required.map((r) => ({ id: r.module_id, name: nameById.get(r.module_id) || 'Modul' })));
            } catch { /* attachments best-effort */ }
        } catch (err) {
            console.error('Failed to load need preview:', err);
            setError('Kunde inte ladda annonsen');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Full-bleed chrome (mirrors the course player) */}
            <div className={`sticky top-0 z-10 transition-colors duration-200 ${scrolled ? 'bg-white border-b border-gray-100 shadow-sm' : 'bg-gray-50/90 backdrop-blur'}`}>
                <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 -ml-2 px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-200/60 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Tillbaka
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A4D4F] bg-[#A4C3B2]/25 px-2.5 py-1 rounded-full">
                        <Eye className="w-3.5 h-3.5" /> Kandidatvy
                    </span>
                    <img src={NEXILINK_WORDMARK_SRC} alt="Nexilink" className="h-7 w-auto object-contain opacity-90" />
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
                {loading ? (
                    <LoadingState message="Laddar förhandsgranskning..." />
                ) : error || !need ? (
                    <ErrorState title="Kunde inte ladda annonsen" message={error || 'Annonsen hittades inte'} onRetry={() => navigate(-1)} />
                ) : (
                    <div className="space-y-5">
                        {/* Intent banner */}
                        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-blue-900">Så här upplever kandidaten annonsen</p>
                                <p className="text-xs text-blue-700">
                                    {courses.length > 0
                                        ? 'Klicka på en förkunskapsmodul nedan för att gå igenom den precis som kandidaten gör.'
                                        : 'Den här annonsen har inga förkunskapsmoduler.'}
                                </p>
                            </div>
                        </div>

                        {/* The posting, as the candidate sees it */}
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <NeedView data={needToView(need, employerDetails?.organisation)}>
                                {/* Required courses — launchable, click-through into the player */}
                                {courses.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                            <GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Förkunskapsmoduler
                                        </h3>
                                        <p className="text-xs text-gray-500 mb-2">Genomförs innan ansökan.</p>
                                        <div className="space-y-2">
                                            {courses.map((c) => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => navigate(`/dashboard/modules/${c.id}?preview=true`)}
                                                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white hover:border-[#1A4D4F]/30 hover:bg-gray-50 transition-colors text-left"
                                                >
                                                    <IconChip icon={BookOpen} tone="brand" size="sm" />
                                                    <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{c.name}</span>
                                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#1A4D4F] flex-shrink-0">
                                                        Gå igenom <ChevronRight className="w-4 h-4" />
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </NeedView>
                        </div>

                        {/* Simulated apply bar — completes the candidate illusion (inert in preview) */}
                        <div className="flex items-center justify-between gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                            <p className="text-xs text-gray-400">I förhandsgranskning kan du inte ansöka.</p>
                            <span className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 text-gray-400 cursor-not-allowed">
                                <Send className="w-4 h-4" /> Ansök
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NeedCandidatePreviewPage;
